"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { Play } from "lucide-react";

import { telemetry } from "@/lib/telemetry";
import { apiClient, type ChangelogRelease } from "@/lib/api-client";

function dateLabel(iso: string): string {
  try {
    return format(parseISO(iso), "MMM d, yyyy");
  } catch {
    return "";
  }
}

export function UpdatesBoard() {
  const pageViewSentRef = useRef(false);

  const [releases, setReleases] = useState<ChangelogRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const loadReleases = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await apiClient.listChangelogReleases({ limit: 50 });
      setReleases(res.releases);
      telemetry.emit("updates.list_loaded", { total: res.total });
    } catch (e) {
      console.error("[updates] failed to load releases", e);
      telemetry.emit("updates.list_error", {
        error_message: e instanceof Error ? e.message : String(e),
      });
      setReleases([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!pageViewSentRef.current) {
      pageViewSentRef.current = true;
      telemetry.emit("updates.page_view", {});
    }
    loadReleases();
  }, [loadReleases]);

  // Content column is capped at 1090px = 170px date rail + 40px gap + 768px
  // (max-w-3xl) body + 2*56px padding. Wider than that leaves dead space on
  // the right and the page looks shifted left of center for logged-out users
  // (no sidebar to mask it).
  return (
    <div className="w-full max-w-[1090px] mx-auto px-6 sm:px-14 pt-6 pb-28">
      {/* Hero: identical pattern to the Library page hero */}
      <div className="mb-10">
        <div className="mb-3 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-400">
            The Changelog
          </span>
        </div>
        <h1 className="text-3xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl">
          Every release, newest first.
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
          What we shipped in each version of Xynth and why it matters.
        </p>
      </div>

      {loading ? (
        <div className="space-y-12" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-[260px] animate-pulse rounded-xl border border-white/[0.06] bg-white/[0.02]"
            />
          ))}
        </div>
      ) : loadError ? (
        <div className="rounded-xl border border-rose-400/20 bg-rose-400/[0.04] px-6 py-16 text-center">
          <p className="text-[15px] font-medium text-white">
            Could not load the changelog.
          </p>
          <p className="mt-1 text-sm text-white/80">
            Something went wrong reaching the server. Please try again.
          </p>
          <button
            type="button"
            onClick={() => loadReleases()}
            className="mt-4 inline-flex items-center rounded-md border border-white/15 bg-white/[0.04] px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60"
          >
            Try again
          </button>
        </div>
      ) : releases.length === 0 ? (
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] px-6 py-16 text-center">
          <p className="text-[15px] font-medium text-white">Nothing here yet.</p>
          <p className="mt-1 text-sm text-white/80">
            New releases will show up here as we ship them.
          </p>
        </div>
      ) : (
        <div>
          {releases.map((release, idx) => (
            <article
              key={release.id}
              className="sm:grid sm:grid-cols-[170px_minmax(0,1fr)] sm:gap-10"
            >
              {idx > 0 && (
                <div
                  className="col-span-full my-12 h-px w-full bg-white/[0.08]"
                  aria-hidden
                />
              )}

              {/* Date rail — sits on the left and sticks while the release
                  content scrolls (Cursor-changelog style). */}
              <div className="sm:pt-1">
                <p className="text-sm font-medium text-white/60 sm:sticky sm:top-8">
                  {dateLabel(release.released_at)}
                </p>
              </div>

              <div className="mt-2 max-w-3xl sm:mt-0">
                <h2 className="text-xl font-bold tracking-tight text-white sm:text-2xl">
                  Xynth {release.version}
                  <span className="mx-2.5 text-white/30">/</span>
                  {release.name}
                </h2>

                {/* Summary */}
                <p className="mt-3 text-[15px] leading-relaxed text-white/85">
                  {release.summary}
                </p>

                {/* Optional featured video, rendered as an elegant clickable row */}
                {release.video_url && (
                  <a
                    href={release.video_url}
                    className="group mt-5 flex items-center gap-4 rounded-xl border border-white/[0.08] bg-white/[0.02] p-3 transition-colors hover:border-white/[0.18] hover:bg-white/[0.04]"
                  >
                    {release.video_thumbnail_url && (
                      <span className="relative block w-36 shrink-0 overflow-hidden rounded-lg border border-white/[0.08] bg-black/50">
                        <img
                          src={release.video_thumbnail_url}
                          alt=""
                          className="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors group-hover:bg-black/25">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-black/60 backdrop-blur-md transition-transform duration-300 group-hover:scale-110">
                            <Play className="ml-0.5 h-3.5 w-3.5 fill-white text-white" />
                          </span>
                        </span>
                      </span>
                    )}
                    <span>
                      <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-400">
                        Watch
                      </span>
                      <span className="mt-1 block text-[15px] font-semibold leading-snug text-white group-hover:text-orange-300 transition-colors">
                        {release.video_caption || "Watch the video"}
                      </span>
                    </span>
                  </a>
                )}

                {/* Feature bullets: same size and left edge as the summary */}
                {release.highlights.length > 0 && (
                  <ul className="mt-5 space-y-3">
                    {release.highlights.map((h) => (
                      <li
                        key={h.title}
                        className="text-[15px] leading-relaxed text-white/85"
                      >
                        <span className="font-semibold text-white">
                          {h.title}.
                        </span>{" "}
                        {h.body}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
